class CartUpsell extends HTMLElement {
  /** @type {{ pointerId: number, startX: number, startScrollLeft: number, track: HTMLElement } | null} */
  #dragState = null;

  /** @type {boolean} */
  #suppressClick = false;

  /** @type {MutationObserver | null} */
  #mutationObserver = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('scroll', this.#handleScroll, true);
    this.addEventListener('pointerdown', this.#handlePointerDown);
    this.addEventListener('pointermove', this.#handlePointerMove);
    this.addEventListener('pointerup', this.#handlePointerEnd);
    this.addEventListener('pointercancel', this.#handlePointerEnd);
    this.addEventListener('wheel', this.#handleWheel, { passive: false });

    this.#mutationObserver = new MutationObserver(this.#scheduleControlUpdate);
    this.#mutationObserver.observe(this, { childList: true, subtree: true });

    this.#resizeObserver = new ResizeObserver(this.#scheduleControlUpdate);
    this.#resizeObserver.observe(this);

    this.#scheduleControlUpdate();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('scroll', this.#handleScroll, true);
    this.removeEventListener('pointerdown', this.#handlePointerDown);
    this.removeEventListener('pointermove', this.#handlePointerMove);
    this.removeEventListener('pointerup', this.#handlePointerEnd);
    this.removeEventListener('pointercancel', this.#handlePointerEnd);
    this.removeEventListener('wheel', this.#handleWheel);
    this.#mutationObserver?.disconnect();
    this.#resizeObserver?.disconnect();
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    if (this.#suppressClick && event.target.closest('[data-cart-upsell-track] a')) {
      event.preventDefault();
      return;
    }

    const button = event.target.closest('[data-cart-upsell-direction]');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;

    const track = this.querySelector('[data-cart-upsell-track]');
    const slide = track?.querySelector('.cart-upsell__slide');
    if (!(track instanceof HTMLElement) || !(slide instanceof HTMLElement)) return;

    const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
    const distance = slide.getBoundingClientRect().width + gap;
    const direction = button.dataset.cartUpsellDirection === 'previous' ? -1 : 1;
    const writingDirection = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    track.scrollBy({ left: direction * writingDirection * distance, behavior });
  };

  #handlePointerDown = (event) => {
    if (!(event.target instanceof Element) || event.pointerType !== 'mouse' || event.button !== 0) return;
    if (event.target.closest('button, input, form')) return;

    const track = event.target.closest('[data-cart-upsell-track]');
    if (!(track instanceof HTMLElement) || !this.contains(track)) return;

    this.#dragState = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startScrollLeft: track.scrollLeft,
      track,
    };
    this.#suppressClick = false;
    track.setPointerCapture(event.pointerId);
  };

  #handlePointerMove = (event) => {
    const state = this.#dragState;
    if (!state || state.pointerId !== event.pointerId) return;

    const distance = event.clientX - state.startX;
    if (Math.abs(distance) > 4) {
      this.#suppressClick = true;
      state.track.classList.add('is-dragging');
      state.track.scrollLeft = state.startScrollLeft - distance;
      event.preventDefault();
    }
  };

  #handlePointerEnd = (event) => {
    const state = this.#dragState;
    if (!state || state.pointerId !== event.pointerId) return;

    state.track.classList.remove('is-dragging');
    if (state.track.hasPointerCapture(event.pointerId)) state.track.releasePointerCapture(event.pointerId);
    this.#dragState = null;
    setTimeout(() => {
      this.#suppressClick = false;
    }, 0);
  };

  #handleWheel = (event) => {
    if (!(event.target instanceof Element)) return;

    const track = event.target.closest('[data-cart-upsell-track]');
    if (!(track instanceof HTMLElement) || !this.contains(track) || track.scrollWidth <= track.clientWidth) return;

    const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
    if (delta === 0) return;

    track.scrollLeft += delta;
    event.preventDefault();
  };

  #handleScroll = (event) => {
    if (event.target instanceof Element && event.target.matches('[data-cart-upsell-track]')) {
      this.#scheduleControlUpdate();
    }
  };

  #scheduleControlUpdate = () => {
    requestAnimationFrame(this.#updateControls);
  };

  #updateControls = () => {
    const track = this.querySelector('[data-cart-upsell-track]');
    const previous = this.querySelector('[data-cart-upsell-direction="previous"]');
    const next = this.querySelector('[data-cart-upsell-direction="next"]');
    if (!(track instanceof HTMLElement)) return;

    const position = Math.abs(track.scrollLeft);
    const maximum = Math.max(0, track.scrollWidth - track.clientWidth);

    if (previous instanceof HTMLButtonElement) previous.disabled = position <= 1;
    if (next instanceof HTMLButtonElement) next.disabled = position >= maximum - 1;
  };
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
